<section
  class="concept-catalog"
  data-testid="terminology-concept-catalog"
  aria-labelledby="concept-catalog-title"
>
  <header class="titlebar">
    <h1 id="concept-catalog-title">Terminology</h1>
    <button
      uiButton="secondary"
      type="button"
      (click)="load()"
      [disabled]="loading || !canRead() || accessLost"
    >
      Refresh
    </button>
    <button
      uiButton
      type="button"
      (click)="create()"
      title="Create a phrase in your current user pack"
      [disabled]="!canAuthor() || !canRead() || accessLost"
    >
      New phrase
    </button>
  </header>
  @if (!canRead() || accessLost) {
    <ui-standard-state
      kind="forbidden"
      message="Terminology is unavailable with your current access."
      recoveryLabel="Check access"
      (recovery)="checkAccess()"
    />
  } @else {
    <form class="toolbar" role="search" (ngSubmit)="applySearch()">
      <label class="search"
        >Find wording or a stable key<input
          uiControl
          type="search"
          name="concept-search"
          data-testid="terminology-concept-search"
          [(ngModel)]="search"
          maxlength="200"
          placeholder="Concept, form or linked phrase"
      /></label>
      <label
        >Inspect context<select
          uiControl
          name="concept-context"
          data-testid="terminology-inspect-context"
          [ngModel]="contextKey"
          (ngModelChange)="changeContext($event)"
          [disabled]="loading || !canPreview()"
        >
          <option value="">Current user</option>
          @for (scope of contextOptions; track scopeKey(scope)) {
            <option [value]="scopeKey(scope)">{{ scopeLabel(scope) }}</option>
          }
        </select></label
      >
      <label
        >Locale / variant<select
          uiControl
          name="concept-variant"
          data-testid="terminology-inspect-variant"
          [ngModel]="variantKey"
          (ngModelChange)="changeVariant($event)"
          [disabled]="loading || !canPreview()"
        >
          <option value="">Current user</option>
          @for (variant of variantOptions; track keyForVariant(variant)) {
            <option [value]="keyForVariant(variant)">
              {{ variantLabel(variant) }}
            </option>
          }
        </select></label
      >
      <button uiButton type="submit" [disabled]="loading">Search</button>
    </form>
    @if (loading) {
      <ui-standard-state
        kind="loading"
        message="Loading authorized terminology catalog…"
      />
    } @else if (error) {
      <ui-standard-state
        kind="error"
        [message]="error"
        recoveryLabel="Retry"
        (recovery)="load()"
      />
    } @else if (catalog; as result) {
      <div class="loaded-context" data-testid="terminology-loaded-context">
        <strong>{{ result.configuration.displayName }}</strong
        ><span>{{ result.configuration.versionCode }}</span>
        <span
          >{{ scopeLabel(result.context) }} ·
          {{ variantLabel(result.context) }}</span
        >
        @if (!result.isCurrentContext) {
          <span class="preview-label">Inspection context</span>
        }
      </div>
      @if (result.authoredDocumentUsageDiscovery && !result.authoredDocumentUsageDiscovery.isComplete) {
        <p class="loaded-context" data-testid="terminology-discovery-coverage">
          Usage discovery includes authorized saved template versions. Local and unsupported sources remain outside this inventory; no matches does not establish that wording is unused.
        </p>
      }
      <div class="workspace">
        <section
          class="group-pane"
          aria-label="Concepts and independent phrases"
        >
          <div class="pane-heading">
            <strong>Concepts and phrases</strong><span>{{ range }}</span>
          </div>
          @if (!result.items.length) {
            <ui-standard-state
              kind="empty"
              [message]="
                search.trim()
                  ? 'No concepts or phrases match this search.'
                  : 'No terminology is registered in this catalog.'
              "
            />
          } @else {
            <div class="groups" #groupList (scroll)="captureScroll($event)">
              @for (group of result.items; track group.groupId) {
                <button
                  type="button"
                  class="group"
                  data-testid="terminology-concept-group"
                  [class.selected]="selectedGroup?.groupId === group.groupId"
                  [attr.aria-pressed]="selectedGroup?.groupId === group.groupId"
                  (click)="selectGroup(group)"
                >
                  <span class="group-heading"
                    ><strong>{{ group.representative.currentText }}</strong
                    ><small>{{
                      group.groupKindCode === "concept"
                        ? "Concept"
                        : "Independent " + group.representative.kindCode
                    }}</small></span
                  >
                  <code>{{ group.representative.tokenCode }}</code>
                  <span class="member-count"
                    >{{ group.members.length }} registered
                    {{
                      group.members.length === 1 ? "definition" : "definitions"
                    }}</span
                  >
                  @if (group.matches.length) {
                    <span class="match" data-testid="terminology-group-match">{{
                      matchLabel(group)
                    }}</span>
                  }
                </button>
              }
            </div>
          }
          @if (totalPages > 1) {
            <nav class="pagination" aria-label="Terminology catalog pages">
              <button
                uiButton="secondary"
                type="button"
                (click)="goToPage(page - 1)"
                [disabled]="page === 1"
              >
                Previous</button
              ><span>Page {{ page }} of {{ totalPages }}</span
              ><button
                uiButton="secondary"
                type="button"
                (click)="goToPage(page + 1)"
                [disabled]="page === totalPages"
              >
                Next
              </button>
            </nav>
          }
        </section>
        <aside class="inspection" aria-label="Wording inspection">
          @if (selectedGroup; as group) {
            <div class="members" aria-label="Registered group definitions">
              @for (member of group.members; track member.definitionId) {
                <button
                  type="button"
                  data-testid="terminology-catalog-member"
                  [class.selected]="
                    selected?.detail?.definition?.definitionId ===
                    member.definitionId
                  "
                  [attr.aria-pressed]="
                    selected?.detail?.definition?.definitionId ===
                    member.definitionId
                  "
                  (click)="selectMember(member.definitionId)"
                >
                  <span>{{ memberLabel(member) }}</span
                  ><strong>{{ member.currentText }}</strong>
                </button>
              }
            </div>
          }
          @if (detailLoading) {
            <ui-standard-state
              kind="loading"
              message="Resolving effective wording…"
            />
          } @else if (detailError) {
            <ui-standard-state
              kind="error"
              [message]="detailError"
              recoveryLabel="Retry"
              (recovery)="retryDetail()"
            />
          } @else if (selected; as selection) {
            <div class="effective" data-testid="terminology-effective-wording">
              <div class="effective-heading">
                <h2>{{ selection.detail.definition.tokenCode }}</h2>
                <span
                  class="resolution"
                  data-testid="terminology-resolution-status"
                  [class.warning]="!resolved"
                  >{{ resolutionLabel }}</span
                >
              </div>
              @if (resolved || selection.effective.usesFallback) {
                <p class="wording">{{ selection.effective.text }}</p>
                <p class="muted">
                  {{
                    selection.effective.usesFallback
                      ? "Declared definition fallback · no selected publication"
                      : "Selected pack publication"
                  }}
                  · {{ selection.configuration.versionCode }} ·
                  {{ variantLabel(selection.context) }}
                </p>
                @if (selection.effective.usesFallback) {
                  <p class="muted">
                    Runtime resolution missing ·
                    {{ selection.effective.reasonCode }}
                  </p>
                }
              } @else {
                <p class="muted">
                  {{ selection.effective.reasonCode }}
                  @if (selection.effective.ambiguousMatchCount) {
                    · {{ selection.effective.ambiguousMatchCount }} matches
                  }
                </p>
                @if (selection.detail.definition.fallbackText.trim()) {
                  <button
                    uiButton="secondary"
                    type="button"
                    (click)="showFallback = !showFallback"
                  >
                    {{ showFallback ? "Hide" : "Show" }} declared product
                    fallback
                  </button>
                  @if (showFallback) {
                    <p
                      class="fallback"
                      data-testid="terminology-product-fallback"
                    >
                      <small
                        >Declared product fallback · runtime resolution remains
                        {{ selection.effective.disposition }}</small
                      ><strong>{{
                        selection.detail.definition.fallbackText
                      }}</strong>
                    </p>
                  }
                } @else {
                  <p class="muted">No product fallback is declared.</p>
                }
              }
              <details
                class="metadata"
                data-testid="terminology-source-details"
              >
                <summary>Identity, source and constraints</summary>
                <dl>
                  <div>
                    <dt>Stable key</dt>
                    <dd>
                      <code>{{ selection.detail.definition.tokenCode }}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Family / form</dt>
                    <dd>
                      {{
                        selection.detail.definition.familyCode ||
                          "No registered family"
                      }}
                      / {{ memberLabel(selection.detail.definition) }}
                    </dd>
                  </div>
                  <div>
                    <dt>Editability</dt>
                    <dd>
                      {{
                        selection.detail.definition.isCustomerEditable
                          ? "Customer editable, subject to server permissions"
                          : "Product governed"
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Catalog representative</dt>
                    <dd>{{ selection.detail.definition.currentText }}</dd>
                  </div>
                  <div>
                    <dt>Declared product fallback</dt>
                    <dd>{{ selection.detail.definition.fallbackText }}</dd>
                  </div>
                  <div>
                    <dt>Selected configuration</dt>
                    <dd>
                      {{ selection.configuration.configurationCode }} /
                      {{ selection.configuration.versionCode }} (#{{
                        selection.configuration.configurationVersionId
                      }})
                    </dd>
                  </div>
                  <div>
                    <dt>Configuration source</dt>
                    <dd>
                      {{ selection.configuration.matchedScopeTypeCode }}
                      {{ selection.configuration.matchedScopeKey }}
                    </dd>
                  </div>
                  <div>
                    <dt>Publication / revision</dt>
                    <dd>
                      {{ selection.effective.publicationId ?? "None" }} /
                      {{ selection.effective.revisionId ?? "None" }}
                    </dd>
                  </div>
                  <div>
                    <dt>Published source text</dt>
                    <dd class="source-text">
                      {{
                        selection.effective.sourceContentText ??
                          "No selected publication"
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Selected source variant</dt>
                    <dd>
                      {{
                        selection.effective.selectedLocaleCode ||
                          "No publication"
                      }}
                      /
                      {{
                        selection.effective.selectedChannelCode ||
                          "All channels"
                      }}
                      /
                      {{
                        selection.effective.selectedAudienceCode ||
                          "All audiences"
                      }}
                      /
                      {{
                        selection.effective.selectedTenantId || "All tenants"
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Effective period</dt>
                    <dd>
                      {{ selection.effective.effectiveStartUtc || "None" }} –
                      {{ selection.effective.effectiveEndUtc || "Open" }}
                    </dd>
                  </div>
                  <div>
                    <dt>Registered length</dt>
                    <dd>
                      {{ selection.detail.constraints.minimumLength ?? 0 }}–{{
                        selection.detail.constraints.maximumLength ?? 2000
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>References</dt>
                    <dd>
                      {{
                        selection.detail.constraints.allowReferences
                          ? "Allowed"
                          : "Not allowed"
                      }}
                      · required:
                      {{
                        selection.detail.constraints.requiredReferenceTokenCodes.join(
                          ", "
                        ) || "None"
                      }}
                      · allowed:
                      {{
                        selection.detail.constraints.allowedReferenceTokenCodes?.join(
                          ", "
                        ) ?? "Registered compatible definitions"
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Owner / classification</dt>
                    <dd>
                      {{ selection.detail.definition.ownerCode }} /
                      {{ selection.detail.definition.classificationCode }}
                    </dd>
                  </div>
                </dl>
                @if (!selection.detail.constraints.isValid) {
                  <p class="warning">
                    Registered constraints are invalid; authoring remains
                    blocked.
                  </p>
                }
              </details>
              @if (selection.isCurrentContext) {
                <button
                  uiButton="secondary"
                  type="button"
                  data-testid="terminology-edit-current-pack"
                  (click)="edit()"
                  [disabled]="!canEditSelection"
                >
                  Edit current pack wording
                </button>
              } @else {
                <p class="muted">
                  This context is for inspection. Return to Current user to edit
                  the current pack.
                </p>
              }
            </div>
          } @else {
            <ui-standard-state
              kind="empty"
              message="Select a concept or phrase to inspect its effective wording."
            />
          }
        </aside>
      </div>
    }
  }
</section>
